import {
  intercept,
  isComputedProp,
  isObservableMap,
  isObservableProp,
  observe,
} from 'mobx';
import type { IChipModel } from '@/interfaces/models';
import type { InterfaceLink } from '@/models/ChipModel';
import type { InterfaceLinkInstanceId } from '@/types/ids';

const positiveInteger = /^[1-9]\d*$/;
const numericLabel = (label: string): bigint | undefined =>
  typeof label === 'string' && positiveInteger.test(label) ? BigInt(label) : undefined;

type Entry = {
  label?: string;
  link: InterfaceLink;
  confirmed: boolean;
  stop?: () => void;
  stopIntercept?: () => void;
  pending: number;
};

/** A synchronous placement batch owns this index; nothing survives the action. */
class LabelAllocation {
  private labels = new Map<string, number>();
  private occupied = new Set<bigint>();
  private successors = new Map<bigint, bigint>();
  private entries = new Map<InterfaceLinkInstanceId, Entry>();
  private stopMap?: () => void;
  private stopMapIntercept?: () => void;
  private customLabels = 0;
  private unconfirmedLabels = 0;
  private pendingNotifications = 0;
  private pendingMapNotifications = 0;
  private disposed = false;

  constructor(readonly links: IChipModel['interfaceLinkMap']) {
    try {
      for (const [id, link] of links) this.add(id, link, true);
      this.stopMapIntercept = intercept(links, (change) => {
        if (!this.disposed) {
          this.pendingMapNotifications++;
          this.pendingNotifications++;
        }
        return change;
      });
      this.stopMap = observe(links, (change) => {
        if (this.disposed) return;
        try {
          this.remove(change.name);
          // Earlier custom observers may have reentered and changed this key.
          if (links.has(change.name)) this.add(change.name, links.get(change.name)!);
        } finally {
          if (this.pendingMapNotifications) {
            this.pendingMapNotifications--;
            this.pendingNotifications--;
          }
        }
      });
    } catch (error) {
      this.dispose();
      throw error;
    }
  }

  private add(id: InterfaceLinkInstanceId, link: InterfaceLink, confirmed = false) {
    const entry: Entry = { pending: 0, link, confirmed };
    if (!confirmed) this.unconfirmedLabels++;
    this.entries.set(id, entry);
    if (isObservableProp(link, 'label') && !isComputedProp(link, 'label')) {
      entry.label = link.label;
      this.addLabel(entry.label);
      entry.stopIntercept = intercept(link, 'label', (change) => {
        if (!this.disposed) {
          entry.pending++;
          this.pendingNotifications++;
        }
        return change;
      });
      entry.stop = observe(link, 'label', () => {
        if (this.disposed) return;
        try {
          if (this.entries.get(id) !== entry) return;
          this.removeLabel(entry.label!);
          entry.label = link.label;
          this.addLabel(entry.label);
        } finally {
          if (entry.pending) {
            entry.pending--;
            this.pendingNotifications--;
          }
        }
      });
    } else {
      // Custom nonobservable/computed labels may change without a map event.
      // Read them afresh on every query rather than assuming they are immutable.
      this.customLabels++;
    }
  }

  private remove(id: InterfaceLinkInstanceId) {
    const entry = this.entries.get(id);
    if (!entry) return;
    if (!entry.confirmed) this.unconfirmedLabels--;
    if (entry.stop) {
      entry.stop();
      entry.stopIntercept?.();
      this.removeLabel(entry.label!);
    } else this.customLabels--;
    this.entries.delete(id);
  }

  confirm(link: InterfaceLink) {
    const entry = this.entries.get(link.id);
    if (!entry || entry.link !== link || entry.confirmed) return;
    // A native createInterfaceLink call has returned: its synchronous hooks are
    // finished. Arbitrary map additions stay on fresh scans, since their label
    // mutation may have begun before we installed the new entry's listeners.
    if (entry.stop) {
      const currentLabel = link.label;
      if (entry.label !== currentLabel) {
        this.removeLabel(entry.label!);
        entry.label = currentLabel;
        this.addLabel(currentLabel);
      }
    }
    entry.confirmed = true;
    this.unconfirmedLabels--;
  }

  private addLabel(label: string) {
    const count = this.labels.get(label) ?? 0;
    this.labels.set(label, count + 1);
    if (count) return;
    const number = numericLabel(label);
    if (number === undefined) return;
    this.occupied.add(number);
    this.successors.set(number, number + 1n);
  }

  private removeLabel(label: string) {
    const count = this.labels.get(label)!;
    if (count > 1) {
      this.labels.set(label, count - 1);
      return;
    }
    this.labels.delete(label);
    const number = numericLabel(label);
    if (number === undefined) return;
    this.occupied.delete(number);
    // A previously skipped number is free again. Compression cannot skip it.
    this.successors.clear();
  }

  find(preferred: string): string {
    // Custom observers registered before ours may query during notification.
    // Interception marks that window before the mutation commits. A later
    // interceptor cancelling a change leaves us conservatively on this path.
    if (this.customLabels || this.unconfirmedLabels || this.pendingNotifications)
      return freshLabel(this.links, preferred);
    let next = positiveInteger.test(preferred) ? BigInt(preferred) : 1n;
    const path: bigint[] = [];
    while (this.occupied.has(next)) {
      path.push(next);
      next = this.successors.get(next) ?? next + 1n;
    }
    for (const number of path) this.successors.set(number, next);
    // Querying does not reserve a number: only an actual map insertion does.
    return String(next);
  }

  dispose() {
    this.disposed = true;
    this.stopMapIntercept?.();
    this.stopMap?.();
    for (const entry of this.entries.values()) {
      entry.stopIntercept?.();
      entry.stop?.();
    }
  }
}

function freshLabel(links: IChipModel['interfaceLinkMap'], preferred: string): string {
  const used = new Set<string>();
  for (const link of links.values()) used.add(link.label);
  let next = positiveInteger.test(preferred) ? BigInt(preferred) : 1n;
  while (used.has(String(next))) next++;
  return String(next);
}

let active: Map<IChipModel, LabelAllocation> | undefined;

/** Share work only while a synchronous placement action is executing. */
export function withInterfaceLabelAllocation<T>(
  chip: IChipModel | undefined,
  run: () => T,
): T {
  if (active) return run();
  const scope = new Map<IChipModel, LabelAllocation>();
  active = scope;
  try {
    // Install before lifecycle hooks can start mutations. Lazy construction in
    // an existing MobX interceptor would miss that mutation's interception.
    if (chip && isObservableMap(chip.interfaceLinkMap))
      scope.set(chip, new LabelAllocation(chip.interfaceLinkMap));
    return run();
  } finally {
    active = undefined;
    for (const allocation of scope.values()) allocation.dispose();
  }
}

export function allocateInterfaceLabel(chip: IChipModel, preferred = '1'): string {
  let allocation = active?.get(chip);
  if (allocation && allocation.links !== chip.interfaceLinkMap) {
    allocation.dispose();
    active!.delete(chip);
    allocation = undefined;
  }
  // Different chips or replaced maps use exact fresh scans for this scope.
  return allocation
    ? allocation.find(preferred)
    : freshLabel(chip.interfaceLinkMap, preferred);
}

/** Confirm only the result of a completed native interface registration. */
export function confirmAllocatedInterfaceLink(chip: IChipModel, link: InterfaceLink) {
  const allocation = active?.get(chip);
  if (allocation?.links === chip.interfaceLinkMap) allocation.confirm(link);
}
